import { useMemo, useState } from "react";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import MoreVert from "@mui/icons-material/MoreVert";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import TableChart from "@mui/icons-material/TableChart";
import {
  Box,
  CircularProgress,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";

import type {
  AuditTrailEntry,
  ExtendedPatient,
  ExtendedThalamosUser,
} from "@thalamos/aspire-common";
import {
  AuditTrailEntryType,
  cto1V1Base,
  cto5V1Base,
  cto7V1Base,
  DischargeReason,
  h3V1Base,
  h5V1Base,
  hpdV1Base,
  isS132RecordingEnabled,
  MhaStatus,
  monitoringFormV1Base,
  s23V1Base,
  s136ExtensionFormV1Base,
  tryGetAuditData,
} from "@thalamos/aspire-common";

import { apiHooks } from "../../../../api.js";
import { entries } from "./Entries.js";
import {
  copyActivityLogToClipboard,
  downloadActivityLogCsv,
  downloadActivityLogPdf,
  extractActivityLogRows,
  formatNhsNumber,
  type ActivityLogTrackingContext,
  type ExportMetadata,
} from "./exportActivityLog.js";
import { FormTitle, PopupDialog } from "~/components/design-system/index.js";
import {
  s132OutcomeLabel,
  s132ProvisionLabel,
  s132WithdrawalReasonLabel,
} from "~/components/s132/S132StatusPill.js";
import { formatMhaStatus } from "~/components/util.js";
import { ukLocalFormatDate, ukLocalFormatDateTime } from "~/util.js";

export function generateMergeMessage(audit: AuditTrailEntry) {
  return tryGetAuditData(audit, AuditTrailEntryType.mergePatient).match(
    (data) => {
      const reason = data.mergeReason ?? data.reason;

      return `${data.mergedPatient || "unknown"} was merged into ${
        data.destinationPatient || "unknown"
      }. ${reason ? `(Reason: ${reason})` : ""}
  ${data.reasonDescription ? `(Description -  ${data.reasonDescription})` : ""}`;
    },
    () => "Patient records were merged.",
  );
}

export function generateUnmergeMessage(audit: AuditTrailEntry) {
  return tryGetAuditData(audit, AuditTrailEntryType.unmergePatient).match(
    (data) => {
      const reason = data.mergeReason ?? data.reason;

      return `${data.destinationPatient || "unknown"} was unmerged from ${
        data.mergedPatient || "unknown"
      }. ${reason ? `(Reason: ${reason})` : ""}
  ${data.reasonDescription ? `(Description -  ${data.reasonDescription})` : ""}`;
    },
    () => "Patient records were unmerged.",
  );
}

export function generateUpdatePatientMessage(audit: AuditTrailEntry) {
  return tryGetAuditData(audit, AuditTrailEntryType.updatePatient).match(
    (data) => {
      const patientFieldChanges = Object.values(data.patientFieldChanges);

      return patientFieldChanges.length > 0
        ? `Updated: patient ${patientFieldChanges.join(", ")} has been updated`
        : "Patient details were reviewed and not updated.";
    },
    () => "Patient details were reviewed and not updated.",
  );
}

export function generateUpdatePatientStateWithManualEventMessage(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.updatePatientStateWithManualEvent,
  ).match(
    (data) => {
      if (data.status === MhaStatus.NotDetained) {
        let dischargeReason: string | undefined =
          data.dischargeReason ?? undefined;
        if (dischargeReason === DischargeReason.Section23) {
          dischargeReason = `discharged ${data.dischargeReason}`;
        }
        if (dischargeReason === DischargeReason.HoldingPowersDischarge) {
          dischargeReason = `discharged from holding powers`;
        }
        if (dischargeReason === DischargeReason.TribunalOrNearestRelative) {
          dischargeReason = `discharged by Tribunal/Nearest Relative`;
        }
        if (dischargeReason === DischargeReason.Section136Outcome) {
          dischargeReason = `discharged from Section 136`;
        }
        return `MHA Status updated to ${
          data.status
        }, Reason: ${dischargeReason ?? "Unknown"}`;
      }

      return `MHA Status manually input as ${formatMhaStatus(data.status) ?? "Unknown"}${data.startDateTime ? `, with a start date time of ${ukLocalFormatDateTime(data.startDateTime)}` : ""}`;
    },
    () => "MHA status manually updated.",
  );
}

export function generateManuallyExtendPatientStateSection136Message(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.manuallyExtendPatientStateSection136,
  ).match(
    (data) => {
      const until = data.expiryDateTime
        ? ukLocalFormatDateTime(data.expiryDateTime)
        : "an unknown date";
      return data.section136ExtensionHours != null
        ? `Section 136 detention manually extended by ${data.section136ExtensionHours} hour${data.section136ExtensionHours === 1 ? "" : "s"}, until ${until}.`
        : `Section 136 detention manually extended, until ${until}.`;
    },
    () => "Section 136 detention manually extended.",
  );
}

export function generateManuallyClearPatientStateSection136ExtensionMessage(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.manuallyClearPatientStateSection136Extension,
  ).match(
    (data) => {
      const until = data.expiryDateTime
        ? ukLocalFormatDateTime(data.expiryDateTime)
        : "an unknown date";
      return `Section 136 extension manually cleared, reset to default end date of ${until}.`;
    },
    () => "Section 136 extension manually cleared, reset to default.",
  );
}

export function generateAutomaticallyExpiredPatientStateMessage(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.automaticallyExpirePatientState,
  ).match(
    (data) =>
      `This patient's MHA status ${data.status} expired at ${ukLocalFormatDateTime(data.expiryDateTime)}`,
    () => "This patient's MHA status expired.",
  );
}

export function generateAutomaticallyPatientStateMessage(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.updatePatientStateAutomatically,
  ).match(
    (data) => {
      // Dates are absent for non-detained statuses; guard before formatting.
      const fmtDate = (value: string | null | undefined) =>
        value ? ukLocalFormatDate(value) : "an unknown date";
      const fmtDateTime = (value: string | null | undefined) =>
        value ? ukLocalFormatDateTime(value) : "an unknown date";
      const formatStatus = formatMhaStatus(data.status);
      if (data.status === MhaStatus.Unknown) return null;

      if (data.status === MhaStatus.Incomplete)
        return `MHA Status ${formatStatus ?? "Unknown"}, data is missing.`;

      if (data.status === MhaStatus.Section3) {
        // Renewal
        if (data.formTemplateId === h5V1Base.id) {
          return `Section 3 renewal of detention form (H5) signed, with a start date of ${fmtDate(data.startDateTime)}. Section 3 detention renewed until ${fmtDate(data.expiryDateTime)}.`;
        }
        // New Section 3 due to CTO revocation
        if (data.formTemplateId === cto5V1Base.id) {
          return `MHA Status updated to ${formatStatus ?? "Unknown"}, revocation of community treatment (CTO5) signed.`;
        }
      }

      if (
        data.formTemplateId === h3V1Base.id &&
        (data.status === MhaStatus.Section4 ||
          data.status === MhaStatus.Section2 ||
          data.status === MhaStatus.Section3)
      ) {
        return `MHA Status updated to ${formatStatus ?? "Unknown"}, ${data.status} record of detention form (H3) signed.`;
      }

      if (data.status === MhaStatus.Section17A) {
        // Initial CTO
        if (data.formTemplateId === cto1V1Base.id) {
          return `Community Treatment Order form (CTO1) signed. CTO effective date of ${fmtDate(data.startDateTime)}.`;
        }
        // Extension
        if (data.formTemplateId === cto7V1Base.id) {
          return `Extension of Community Treatment Order form (CTO7) signed, with a start date of ${fmtDate(data.startDateTime)}. CTO extended until ${fmtDate(data.expiryDateTime)}.`;
        }
      }

      if (data.status === MhaStatus.NotDetained) {
        switch (data.formTemplateId) {
          case s23V1Base.id:
            return `MHA Status updated to ${formatStatus ?? "Unknown"}, Section 23 form signed.`;
          case monitoringFormV1Base.id:
            return `MHA Status updated to ${formatStatus ?? "Unknown"}, Section 136 outcome form signed.`;
          case hpdV1Base.id:
            return `MHA Status updated to ${formatStatus ?? "Unknown"}, discharge from holding powers form signed.`;
          default:
            return `MHA Status updated to ${formatStatus ?? "Unknown"}, discharge form signed.`;
        }
      }

      if (data.status === MhaStatus.Section136) {
        if (data.formTemplateId === s136ExtensionFormV1Base.id) {
          // Section 136 is hours-based, so show date and time.
          return `S136 extension form signed. Section 136 detention extended until ${fmtDateTime(data.expiryDateTime)}.`;
        }
        // Trigger (Section 136 form sent by a police officer via Vision incident accept).
        const forcePart = data.forceName ? ` (${data.forceName})` : "";
        return `MHA Status updated to 'Section 136'. Section 136 form sent by ${data.officerName ?? "Unknown"}${forcePart}.`;
      }

      return `MHA Status updated to ${formatStatus ?? "Unknown"}, ${formatStatus ?? "Unknown"} form signed.`;
    },
    () => null,
  );
}

export function generateSection136ClockCorrectedMessage(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.section136ClockCorrected,
  ).match(
    (data) => {
      // Section 136 is hours-based, so its end is only meaningful to the minute.
      const expiry = data.expiryDateTime
        ? ukLocalFormatDateTime(data.expiryDateTime)
        : "an unknown date";
      const force = data.forceName ? ` (${data.forceName})` : "";
      // The stored row need not carry an officer; the corrected expiry is the
      // point of the entry, so the message reads without an attribution.
      const by = data.officerName ? ` by ${data.officerName}${force}` : "";
      return `MHA status Section 136 expiry updated to ${expiry}. Earlier place of safety arrival recorded${by}.`;
    },
    () => "An earlier place of safety arrival updated the Section 136 expiry.",
  );
}

export function generateUpdatePatientDemographicsPullEvent(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.updatePatientDemographicsPullEvent,
  ).match(
    (data) => {
      if (data.processed === true) {
        return null;
      }

      const system = data.externalSystemType === "pds" ? "PDS" : "Rio";

      const rioInstanceName = audit.resources.filter(
        (r) => r.resourceType === "rioInstance",
      )?.[0]?.resourceName;

      let eventMessage = `Patient details synchronised with ${system}`;
      if (rioInstanceName !== undefined) {
        eventMessage += ` (${rioInstanceName})`;
      }
      eventMessage += ". Available updates were ignored/skipped:";

      const differences: string[] = [];
      if (data.differences && data.differences.length > 0) {
        const differenceTypeNames: Record<string, string> = {
          dob: "date of birth",
          deathNotificationStatus: "death notification status",
        };
        differences.push(
          ...data.differences.map(
            (diff) => differenceTypeNames[diff.type] ?? diff.type,
          ),
        );
      }

      if (differences.length > 0) {
        eventMessage += ` ${differences.join(", ")}`;
      }

      return eventMessage;
    },
    () => null,
  );
}

export function generateDualMhaStatusDetectedMessage(audit: AuditTrailEntry) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.dualMhaStatusDetected,
  ).match(
    (data) =>
      `Coexisting MHA statuses detected: ${formatMhaStatus(data.coexistingStatus)} is coexisting with ${formatMhaStatus(data.existingStatus)}.`,
    () => "Coexisting MHA statuses detected.",
  );
}

export function generateCoexistingMhaStatusClearedMessage(
  audit: AuditTrailEntry,
) {
  return tryGetAuditData(
    audit,
    AuditTrailEntryType.coexistingMhaStatusCleared,
  ).match(
    (data) =>
      `Coexisting MHA status cleared: status updated to ${formatMhaStatus(data.remainingStatus)} only.`,
    () => "Coexisting MHA status cleared.",
  );
}

const s132AttemptSummary = (data: {
  section?: string | null;
  outcome?: string | null;
  occurredAt?: string | null;
  writtenInformationGiven?: boolean | null;
  deliveredBySomeoneElse?: boolean | null;
}) =>
  [
    data.section ? `for ${data.section}` : null,
    data.outcome ? s132OutcomeLabel(data.outcome).toLowerCase() : null,
    data.occurredAt ? `given ${ukLocalFormatDateTime(data.occurredAt)}` : null,
    data.writtenInformationGiven === true
      ? "with written information"
      : data.writtenInformationGiven === false
        ? "without written information"
        : null,
    data.deliveredBySomeoneElse ? "given by someone else" : null,
  ]
    .filter(Boolean)
    .join(", ");

const withLead = (lead: string, text: string): string =>
  text ? `${lead}${text}` : "";

export function generateS132Message(audit: AuditTrailEntry) {
  if (audit.type === AuditTrailEntryType.recordS132Attempt) {
    return tryGetAuditData(audit, AuditTrailEntryType.recordS132Attempt).match(
      (data) =>
        `${s132ProvisionLabel(data.section)} information attempt recorded${withLead(" ", s132AttemptSummary(data))}.`,
      () => "Section 132 information attempt recorded.",
    );
  }
  if (audit.type === AuditTrailEntryType.correctS132Attempt) {
    return tryGetAuditData(audit, AuditTrailEntryType.correctS132Attempt).match(
      (data) =>
        `${s132ProvisionLabel(data.section)} information attempt amended${withLead(", now ", s132AttemptSummary(data))}.`,
      () => "Section 132 information attempt amended.",
    );
  }
  return tryGetAuditData(audit, AuditTrailEntryType.withdrawS132Attempt).match(
    (data) =>
      `${s132ProvisionLabel(data.section)} information attempt deleted${
        data.section ? ` for ${data.section}` : ""
      }${
        data.reason
          ? `: ${s132WithdrawalReasonLabel(data.reason).toLowerCase()}`
          : ""
      }.`,
    () => "Section 132 information attempt deleted.",
  );
}

export function getPatientActivityMessageFn(
  typesToExclude: AuditTrailEntryType[],
) {
  return (audit: AuditTrailEntry): string | string[] | null => {
    if (typesToExclude.includes(audit.type)) {
      return null;
    }

    switch (audit.type) {
      case AuditTrailEntryType.createPatient: {
        const patientName = audit?.resources.find(
          (r) => r.resourceType === "patient",
        )?.resourceName;

        const source = tryGetAuditData(
          audit,
          AuditTrailEntryType.createPatient,
        ).match(
          (data) => data.source,
          () => undefined,
        );

        if (source === "vision") {
          return `Patient ${patientName} was created from details shared by police.`;
        }

        if (source === "rio") {
          return `Patient ${patientName} was created via Rio`;
        }

        if (source === "pds") {
          return `Patient ${patientName} was created via PDS`;
        }

        if (source === "bulkImport") {
          return `Patient ${patientName} was created via bulk import`;
        }

        return `Patient ${patientName} was created`;
      }

      case AuditTrailEntryType.mergePatient:
        return generateMergeMessage(audit);

      case AuditTrailEntryType.unmergePatient:
        return generateUnmergeMessage(audit);

      case AuditTrailEntryType.updatePatient:
        return generateUpdatePatientMessage(audit);

      case AuditTrailEntryType.updatePatientStateWithManualEvent:
        return generateUpdatePatientStateWithManualEventMessage(audit);

      case AuditTrailEntryType.automaticallyExpirePatientState:
        return generateAutomaticallyExpiredPatientStateMessage(audit);

      case AuditTrailEntryType.updatePatientStateAutomatically:
        return generateAutomaticallyPatientStateMessage(audit);

      case AuditTrailEntryType.section136ClockCorrected:
        return generateSection136ClockCorrectedMessage(audit);

      case AuditTrailEntryType.dualMhaStatusDetected:
        return generateDualMhaStatusDetectedMessage(audit);

      case AuditTrailEntryType.coexistingMhaStatusCleared:
        return generateCoexistingMhaStatusClearedMessage(audit);

      case AuditTrailEntryType.updatePatientDemographicsPullEvent:
        return generateUpdatePatientDemographicsPullEvent(audit);

      case AuditTrailEntryType.patientSearchReasoning:
        return tryGetAuditData(
          audit,
          AuditTrailEntryType.patientSearchReasoning,
        ).match(
          (data) => `${
            data.reason
              ? `
        Patient record accessed via search under the following reason: ${data.reason}`
              : ""
          }
        ${data.reasonDescription ? `- ${data.reasonDescription}` : ""}`,
          () => null,
        );

      case AuditTrailEntryType.createPatientDemographicsAlert:
        return tryGetAuditData(
          audit,
          AuditTrailEntryType.createPatientDemographicsAlert,
        ).match(
          (data) =>
            data.type === "invalidated"
              ? "Updated: The NHS number on this record was no longer valid and was decoupled from the Spine/PDS."
              : null,
          () => null,
        );

      case AuditTrailEntryType.clearPatientDemographicsAlerts:
        return "Patient demographics alerts were cleared";
      case AuditTrailEntryType.createSuggestedPatientMerge:
        return "A suggested patient merge was created for this patient (marked as superseded on Spine)";
      case AuditTrailEntryType.actionSuggestedPatientMerge:
        return "A suggested patient merge was actioned by a demographics manager";
      case AuditTrailEntryType.patientSupersededNhsNumberUpdated:
        return "Updated: The NHS number on this record was replaced (superseded on Spine) ";
      case AuditTrailEntryType.manuallyOverridePatientStateExpiryDate:
        return tryGetAuditData(
          audit,
          AuditTrailEntryType.manuallyOverridePatientStateExpiryDate,
        ).match(
          (data) =>
            data.reason
              ? `MHA status end date manually edited to ${data.expiryDateTime ? ukLocalFormatDateTime(data.expiryDateTime) : "an unknown date"}, for the following reason: ${data.reason}. Additional information provided: ${data.reasonDescription}`
              : `MHA status end date manually reset to default value.`,
          () => `MHA status end date manually updated.`,
        );
      case AuditTrailEntryType.recordS132Attempt:
      case AuditTrailEntryType.correctS132Attempt:
      case AuditTrailEntryType.withdrawS132Attempt:
        return generateS132Message(audit);
      case AuditTrailEntryType.manuallyExtendPatientStateSection136:
        return generateManuallyExtendPatientStateSection136Message(audit);
      case AuditTrailEntryType.manuallyClearPatientStateSection136Extension:
        return generateManuallyClearPatientStateSection136ExtensionMessage(
          audit,
        );
      default:
        return null;
    }
  };
}

export function patientActivityTypesToExclude(
  user: ExtendedThalamosUser,
): AuditTrailEntryType[] {
  return [
    ...(user.sessionOrganisationConfiguration?.patientStateEnabled
      ? []
      : [
          AuditTrailEntryType.updatePatientStateAutomatically,
          AuditTrailEntryType.section136ClockCorrected,
          AuditTrailEntryType.updatePatientStateWithManualEvent,
          AuditTrailEntryType.automaticallyExpirePatientState,
          AuditTrailEntryType.dualMhaStatusDetected,
          AuditTrailEntryType.coexistingMhaStatusCleared,
        ]),
    ...(isS132RecordingEnabled(user)
      ? []
      : [
          AuditTrailEntryType.recordS132Attempt,
          AuditTrailEntryType.correctS132Attempt,
          AuditTrailEntryType.withdrawS132Attempt,
        ]),
  ];
}

export function PatientActivityLog({
  patientId,
  patient,
  user,
  onClose,
}: {
  patientId: string;
  patient: ExtendedPatient;
  user: ExtendedThalamosUser;
  onClose: () => void;
}) {
  const [{ data: auditData, loading: auditDataLoading }] =
    apiHooks.audit.searchByPatientId(patientId, {
      limit: 1000,
      offset: 0,
      fromDate: "2000-01-01",
      toDate: "2050-01-01",
    });

  const titleText = "Activity Log for patient";

  const getMessageForPatient = useMemo(
    () => getPatientActivityMessageFn(patientActivityTypesToExclude(user)),
    [user],
  );

  const allEntries = useMemo(
    () => (auditDataLoading ? [] : (auditData?.entries ?? [])),
    [auditDataLoading, auditData],
  );

  const exportRows = useMemo(
    () =>
      !auditDataLoading
        ? extractActivityLogRows(allEntries, getMessageForPatient)
        : [],
    [auditDataLoading, allEntries, getMessageForPatient],
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const tracking: ActivityLogTrackingContext = useMemo(
    () => ({
      type: "patient",
      patientId,
    }),
    [patientId],
  );

  const metadata: ExportMetadata = useMemo(
    () => ({
      patientName: `${patient.name.family.toUpperCase()}, ${patient.name.given}`,
      nhsNumber: formatNhsNumber(patient.nhsNumber),
    }),
    [patient.name.given, patient.name.family, patient.nhsNumber],
  );

  const handleExportCopy = () => {
    setMenuAnchorEl(null);
    void copyActivityLogToClipboard(exportRows, tracking, metadata);
  };

  const handleExportCsv = () => {
    setMenuAnchorEl(null);
    downloadActivityLogCsv(exportRows, titleText, tracking, metadata);
  };

  const handleExportPdf = () => {
    setMenuAnchorEl(null);
    downloadActivityLogPdf(exportRows, titleText, tracking, metadata);
  };

  return (
    <PopupDialog open={true} onClose={onClose} fullWidth>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
        }}
      >
        <FormTitle
          titleText={titleText}
          hasContainerMarginBottom={false}
          hasTitleBottomMargin={false}
          useReducedTopPadding={true}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <IconButton
            aria-label="Export activity log"
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            disabled={auditDataLoading}
            data-testid="export-activity-log-button"
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={() => setMenuAnchorEl(null)}
          >
            <MenuItem onClick={handleExportCopy}>
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy to clipboard</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportCsv}>
              <ListItemIcon>
                <TableChart fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportPdf}>
              <ListItemIcon>
                <PictureAsPdf fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as PDF</ListItemText>
            </MenuItem>
          </Menu>
          <IconButton
            sx={{ width: "40px", height: "40px" }}
            data-testid="close-dialog"
            aria-label="Close"
            onClick={onClose}
          >
            <Close sx={{ color: "primary.hint" }} fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <DialogContent>
        {auditDataLoading ? (
          <CircularProgress />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {entries(user, allEntries, getMessageForPatient)}
          </Box>
        )}
      </DialogContent>
    </PopupDialog>
  );
}
